import { naturalSortKey, type CopyListItem } from '@vinyl/shared';

/**
 * Search, filters, sort and totals over the Collection list, in the browser
 * (ADR 0005). Pure functions so they are fast to test and easy to move to a
 * Web Worker if the budgets ever need it.
 */

export type SortKey = 'added' | 'artist' | 'title' | 'year' | 'acquired' | 'location';
export type HistoryMode = 'hide' | 'show' | 'only';

export type BrowseState = {
  q?: string;
  sort?: SortKey;
  dir?: 'asc' | 'desc';
  format?: string[];
  genre?: string[];
  decade?: string[];
  country?: string[];
  tag?: string[];
  /** Household ids, and NO_HOUSEHOLD for Copies without one. */
  household?: string[];
  location?: string[];
  history?: HistoryMode;
  status?: CopyListItem['status'];
};

/** The filter value for Copies that have no Household. */
export const NO_HOUSEHOLD = 'none';

/** Lower case with accents removed, so "bjork" finds "Björk". */
export function fold(text: string): string {
  return text.normalize('NFKD').replace(/\p{M}+/gu, '').toLocaleLowerCase('en');
}

/** One string per Copy holding everything search looks at. */
export function searchText(c: CopyListItem): string {
  const r = c.release;
  return fold(
    [r.artistsDisplay, r.title, ...r.tracks, ...r.labels, ...r.catnos, ...r.barcodes, ...c.tags].join('\n'),
  );
}

export type Indexed = { copy: CopyListItem; text: string };

export function indexCopies(copies: CopyListItem[]): Indexed[] {
  return copies.map((copy) => ({ copy, text: searchText(copy) }));
}

export const decadeOf = (year: number | null) => (year ? `${Math.floor(year / 10) * 10}s` : null);

function anyOf(selected: string[] | undefined, values: (string | null)[]): boolean {
  if (!selected || selected.length === 0) return true;
  return values.some((v) => v !== null && selected.includes(v));
}

export function matches(item: Indexed, state: BrowseState): boolean {
  const c = item.copy;
  const history = state.history ?? 'hide';
  if (state.status) {
    if (c.status !== state.status) return false;
  } else if (history === 'hide' && c.status !== 'owned') return false;
  else if (history === 'only' && c.status === 'owned') return false;
  if (state.q) {
    const words = fold(state.q).split(/\s+/).filter(Boolean);
    if (!words.every((w) => item.text.includes(w))) return false;
  }
  const r = c.release;
  return (
    anyOf(state.format, r.formats) &&
    anyOf(state.genre, r.genres) &&
    anyOf(state.decade, [decadeOf(r.year)]) &&
    anyOf(state.country, [r.country]) &&
    anyOf(state.tag, c.tags) &&
    anyOf(state.household, [c.household?.id ?? NO_HOUSEHOLD]) &&
    anyOf(state.location, [c.location])
  );
}

const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

function compareNullable<T>(a: T | null, b: T | null, cmp: (x: T, y: T) => number): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return cmp(a, b);
}

const householdKey = (c: CopyListItem): string | null => (c.household ? (c.householdSort ?? naturalSortKey(c.household.name)) : null);

/** Default: date added, newest first. Missing values always go last. */
export function compare(sort: SortKey = 'added', dir: 'asc' | 'desc' = sort === 'added' ? 'desc' : 'asc') {
  const sign = dir === 'asc' ? 1 : -1;
  const byArtist = (a: CopyListItem, b: CopyListItem) =>
    collator.compare(a.release.artistsSort, b.release.artistsSort) || collator.compare(a.release.title, b.release.title);
  return (a: CopyListItem, b: CopyListItem): number => {
    let primary = 0;
    switch (sort) {
      case 'added':
        primary = sign * a.createdAt.localeCompare(b.createdAt);
        break;
      case 'artist':
        primary = sign * byArtist(a, b);
        break;
      case 'title':
        primary = sign * collator.compare(a.release.title, b.release.title);
        break;
      case 'year':
        primary = compareNullable(a.release.year, b.release.year, (x, y) => sign * (x - y));
        break;
      case 'acquired':
        primary = compareNullable(a.acquired?.date ?? null, b.acquired?.date ?? null, (x, y) => sign * x.localeCompare(y));
        break;
      case 'location': {
        // Place: Household by name (none after all Households, flipped when descending),
        // then shelf in natural order, then artist.
        const ha = householdKey(a);
        const hb = householdKey(b);
        if (ha !== hb) {
          primary = ha === null ? sign : hb === null ? -sign : sign * ha.localeCompare(hb);
          break;
        }
        primary = compareNullable(a.locationSort ?? (a.location ? naturalSortKey(a.location) : null), b.locationSort ?? (b.location ? naturalSortKey(b.location) : null), (x, y) => sign * x.localeCompare(y));
        break;
      }
    }
    return primary || byArtist(a, b) || a.id.localeCompare(b.id);
  };
}

export function browse(items: Indexed[], state: BrowseState): CopyListItem[] {
  return items.filter((i) => matches(i, state)).map((i) => i.copy).sort(compare(state.sort, state.dir));
}

export type Totals = { shown: number; owned: number; spent: { currency: string; amount: number }[] };

/** Count of owned Copies shown, of all owned, and spent per currency for the shown owned Copies. */
export function totals(all: CopyListItem[], shown: CopyListItem[]): Totals {
  const ownedShown = shown.filter((c) => c.status === 'owned');
  const spent = new Map<string, number>();
  for (const c of ownedShown) if (c.price) spent.set(c.price.currency, (spent.get(c.price.currency) ?? 0) + c.price.amount);
  return {
    shown: ownedShown.length,
    owned: all.filter((c) => c.status === 'owned').length,
    spent: [...spent].map(([currency, amount]) => ({ currency, amount: Math.round(amount * 100) / 100 })).sort((a, b) => b.amount - a.amount),
  };
}

/** Filter choices with how many Copies each would show. */
/** Households that have Copies in the view, by name, then "none" when some Copies have no Household. */
function householdFacet(items: Indexed[], state: BrowseState) {
  const base = items.filter((i) => matches(i, { ...state, household: undefined }));
  const counts = new Map<string, { label: string; n: number }>();
  let none = 0;
  for (const { copy } of base) {
    if (!copy.household) none += 1;
    else counts.set(copy.household.id, { label: copy.household.name, n: (counts.get(copy.household.id)?.n ?? 0) + 1 });
  }
  // A chosen Household stays in the list when no Copy of it shows any more, so it can be unchosen.
  for (const id of state.household ?? []) {
    if (id === NO_HOUSEHOLD || counts.has(id)) continue;
    const known = items.find((i) => i.copy.household?.id === id)?.copy.household;
    if (known) counts.set(id, { label: known.name, n: 0 });
  }
  const named = [...counts].sort((a, b) => collator.compare(a[1].label, b[1].label)).map(([value, { label, n }]) => ({ value, label, n }));
  return none > 0 || state.household?.includes(NO_HOUSEHOLD) ? [...named, { value: NO_HOUSEHOLD, label: null, n: none }] : named;
}

export function facets(items: Indexed[], state: BrowseState) {
  const count = (key: keyof BrowseState, values: (c: CopyListItem) => (string | null)[]) => {
    const base = items.filter((i) => matches(i, { ...state, [key]: undefined }));
    const counts = new Map<string, number>();
    for (const i of base) for (const v of new Set(values(i.copy))) if (v) counts.set(v, (counts.get(v) ?? 0) + 1);
    return [...counts].sort((a, b) => collator.compare(a[0], b[0])).map(([value, n]) => ({ value, n }));
  };
  return {
    format: count('format', (c) => c.release.formats),
    genre: count('genre', (c) => c.release.genres),
    decade: count('decade', (c) => [decadeOf(c.release.year)]),
    country: count('country', (c) => [c.release.country]),
    tag: count('tag', (c) => c.tags),
    household: householdFacet(items, state),
    location: count('location', (c) => [c.location]),
  };
}

export function hasFilters(state: BrowseState): boolean {
  return Boolean(
    state.q || state.format?.length || state.genre?.length || state.decade?.length || state.country?.length || state.tag?.length || state.household?.length || state.location?.length || state.status,
  );
}
